import { bool, func, node, oneOf, string } from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { styleOverrideReason } from '@ids/core';
import { getStyle as cardSmallGet } from '@ids/ddms-styled/comps/cardSmall/intuit/light/index';
import { getStyle as cardXsmallGet } from '@ids/ddms-styled/comps/cardXsmall/intuit/light/index';
import { getStyle as cardStandardGet } from '@ids/ddms-styled/comps/cardStandard/intuit/light/index';

/**
 * @param props - props
 * */
const height = props => {
  switch (props.size) {
    case 'none':
      return 'unset';
    case 'xsmall':
      return cardXsmallGet('background.height');
    case 'small':
      return cardSmallGet('background.height');
    case 'standard':
    default:
      return cardStandardGet('background.height');
  }
};

const Wrapper = styled.div`
  margin: 0 auto;
  background: ${cardStandardGet('background.color')};
  border-radius: ${cardStandardGet('background.radius')};
  border: ${cardStandardGet('border.stroke')} solid
    ${cardStandardGet('border.color')};
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  outline: none;
  height: ${props => height(props)};
  ${({ disableCardClick }) =>
    !disableCardClick &&
    `
  &:hover {
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2);
  }
  &:focus {
    // TODO should be 'highlight.opacity'
    box-shadow: 0 0 0 ${cardStandardGet(
      'highlight.stroke'
    )} rgba(0, 119, 197, 0.5), 0 2px 8px 0 rgba(0, 0, 0, 0.2);
  }
  `};
`;

/**
 * @param props - props to be passed to Card Component
 */
const Card = props => {
  const {
    automationId,
    children,
    disableCardClick,
    onClick,
    ...restProps
  } = props;

  /**
   * @param event - event
   * */
  const handleKeyPress = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const otherProps = disableCardClick
    ? { tabIndex: '-1' }
    : {
        onClick,
        onKeyPress: handleKeyPress,
        role: 'button',
        tabIndex: '0'
      };

  return (
    <Wrapper
      data-automation-id={automationId}
      disableCardClick={disableCardClick}
      {...restProps}
      {...otherProps}
    >
      {children}
    </Wrapper>
  );
};

Card.propTypes = {
  /**
   * Component ID
   */
  automationId: string,
  /**
   * Inner content of card
   */
  children: node,
  /**
   * Disables focus on Card container, only allowing interactive content to be focusable
   */
  // eslint-disable-next-line react/boolean-prop-naming
  disableCardClick: bool,
  /**
   * ID attribute for an element
   */
  id: string,
  /**
   * Callback when card is clicked
   */
  onClick: func,
  /**
   * Size of Card: either standard, small or xsmall
   */
  size: oneOf(['none', 'standard', 'small', 'xsmall']),
  /**
   * When `style` or `className` prop is provided, provide the reason behind the override.
   */
  styleOverrideReason
};

Card.defaultProps = {
  automationId: undefined,
  children: null,
  disableCardClick: false,
  id: undefined,
  onClick: () => {},
  size: 'standard',
  styleOverrideReason: undefined
};

Card.displayName = 'Card';

export default Card;
